// src/com/hook/appPrompters.js
import React, { createContext, useState, useCallback, useRef, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, Typography,
  Paper,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReportIcon from '@mui/icons-material/Report';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AppPalette, AppWord, ErrorMsg } from '../../model/AppConst';
import { NoUser, userInfoRepo } from '../../model/CvoModel';

const IconStyle = { marginRight: 8, color: 'white' };
const TYPE_ALERT = 'alert';
const TYPE_CONFIRM = 'confirm';

// 드래그 가능한 Paper 컴포넌트
const DraggablePaper = (props) => {
    const nodeRef = useRef(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [rel, setRel] = useState(null); // 클릭한 시점의 상대 좌표

    const onMouseDown = (e) => {
        // DialogTitle(h2) 영역에서만 드래그가 시작되도록 제한 (선택 사항)
        if (e.target.tagName !== 'H2' && !e.target.classList.contains('MuiDialogTitle-root')) return;
        
        setDragging(true);
        const pos = nodeRef.current.getBoundingClientRect();
        setRel({
            x: e.pageX - pos.left,
            y: e.pageY - pos.top,
        });
        e.stopPropagation();
        e.preventDefault();
    };

    const onMouseMove = (e) => {
        if (!dragging) return;
        setPosition({
            x: e.pageX - rel.x - (window.innerWidth - nodeRef.current.offsetWidth) / 2,
            y: e.pageY - rel.y - (window.innerHeight - nodeRef.current.offsetHeight) / 2,
        });
        e.stopPropagation();
        e.preventDefault();
    };

    const onMouseUp = () => {
        setDragging(false);
    };

    useEffect(() => {
        if (dragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        } else {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [dragging]);

    return (
        <Paper
            {...props}
            ref={nodeRef}
            onMouseDown={onMouseDown}
            style={{
                ...props.style,
                position: 'relative',
                transform: `translate(${position.x}px, ${position.y}px)`,
                cursor: dragging ? 'grabbing' : 'auto',
            }}
        />
    );
};

export const PromptContext = createContext(null);

/**
 * Confirm, Alert, ResponseAlert 등 앱 전역에서 사용할 수 있는 프롬프트 제공자.
 * 일단 만들기는 했는데, 기존에 만들어 둔 것들이 너무 얼기설기 퍼져 있어서, 이걸로 다 대체할 수 있을지는 모르겠다.
 * 일단은 새로 만들 때는 이걸 사용하고, 기존에 만들어 둔 것들은 점진적으로 대체하는 방향으로 가는 게 좋을 듯.
 * @param {*} param0 
 * @returns 
 */
export const PromptProvider = ({ children }) => {
    const [prompts, setPrompts] = useState([]);

    // 공통: 팝업 제거 함수
    const removePrompt = useCallback((id) => {
        setPrompts((prev) => prev.filter((p) => p.id !== id));
    }, []);

    // 1. addAlert
    const addAlert = useCallback(({ title, message, option = {} }) => {
        const id = Date.now() + Math.random();
        setPrompts((prev) => [...prev, {
            id,
            type: TYPE_ALERT,
            title,
            message: Array.isArray(message) ? message : [message], // 배열로 통일
            buttonLabel: option.buttonLabel || '확인'
        }]);
    }, []);
    /*
    const { addAlert } = useContext(PromptContext);

    addAlert({
        title: '공지사항',
        message: ['서버 점검 예정입니다.', '오후 2시부터 4시까지 이용이 제한됩니다.'],
        option: { buttonLabel: '닫기' }
    });
    */

    // 2. responseAlert (GraphQL/Apollo 에러 처리용)
    const responseAlert = useCallback(({ error, title }) => {
        let alertTitle = title || '오류';
        let message = [];
        if (error) {
            if (error.graphQLErrors?.length > 0) {
                message = error.graphQLErrors.map(e => e.message);
            }
            else if(error.message) {
                if(error.message.indexOf(ErrorMsg.AUTH_ERROR) >= 0) {
                    message.push("로그아웃 되었습니다. 다시 로그인 해 주세요");
                    message.push("오랫동안 활동이 없는 경우 그럴 수 있습니다.");
                    alertTitle = "로그인상태가 아닙니다.";
                    userInfoRepo(NoUser);
                }
                else message = [error.message];
            }
            else {
                message = ["알 수 없는 오류가 발생했습니다. [no message in error]"];
            }
        }
        else {
            message = ["알 수 없는 오류가 발생했습니다.[unspecified error]"];
        }
        addAlert({ title: alertTitle, message });
    }, [addAlert]);
    /*
    const { responseAlert } = useContext(PromptContext);

    const [getCustInfoList] = useLazyQuery(LIST_CUST_INFO, {
        onError: (error) => { 
            responseAlert({ error: error, title: '고객목록 조회 오류' });
        }
    });
    */

    // 3. openConfirmDialog (Promise를 사용하여 await 가능하게 구현)
    const openConfirmDialog = useCallback(({ title, message, option = {} }) => {
        const id = Date.now() + Math.random();
        return new Promise((resolve) => {
            setPrompts((prev) => [...prev, {
                id,
                type: TYPE_CONFIRM,
                title,
                message: Array.isArray(message) ? message : [message], // 배열로 통일
                yesLabel: option.yesLabel || '예',
                noLabel: option.noLabel || '아니오',
                resolve // 여기서 Promise를 해결함
            }]);
        });
    }, []);
    /*
    const { openConfirmDialog } = useContext(PromptContext);

    const handleDelete = async () => {
        const ok = await openConfirmDialog({
            title: '삭제 확인',
            message: '정말로 삭제하시겠습니까?',
            option: { yesLabel: '삭제', noLabel: '취소' }
        });

        if (ok) {
            console.log("삭제 진행...");
        }
    };
    */

    return (
        <PromptContext.Provider value={{ addAlert, responseAlert, openConfirmDialog }}>
            {children}
            {/* 여러 개의 다이얼로그 렌더링 */}
            {prompts.map((prompt, index) => (
                <Dialog
                    key={prompt.id}
                    open={true}
                    onClose={() => {
                        if (prompt.type === TYPE_CONFIRM) prompt.resolve(false);
                        removePrompt(prompt.id);
                    }}
                    PaperComponent={DraggablePaper} // 드래그 가능하게 설정
                    hideBackdrop={index > 0} // 여러 개 뜰 때 배경 겹침 방지
                    sx={{ '& .MuiDialogTitle-root': { cursor: 'grab', userSelect: 'none' } }}
                >
                    <DialogTitle
                        style={{
                            backgroundColor: AppPalette.WarnRGB,
                            color: 'white', minWidth: 300, display: 'flex', alignItems: 'center',
                            cursor: 'move'
                        }}
                    >
                        {
                            prompt.type === TYPE_ALERT ?
                            <ReportIcon style={IconStyle} fontSize={AppWord.LARGE}/> :
                            <HelpOutlineIcon style={IconStyle} fontSize={AppWord.LARGE}/>
                        }
                        {prompt.title}
                    </DialogTitle>
                    <DialogContent dividers>
                        {
                            prompt.message.map((msg, i) => (
                                <Typography key={i} variant="body1" gutterBottom>{msg}</Typography>
                            ))
                        }
                    </DialogContent>
                    <DialogActions>
                        {prompt.type === TYPE_ALERT ? (
                        <Button
                            onClick={() => removePrompt(prompt.id)}
                            variant={AppPalette.VariantContained}
                            autoFocus
                            startIcon={<CheckIcon />}
                        >
                            {prompt.buttonLabel}
                        </Button>
                        )
                        :
                        (
                        <>
                        <Button
                            onClick={() => {
                                prompt.resolve(true);
                                removePrompt(prompt.id);
                            }}
                            variant={AppPalette.VariantContained} autoFocus
                            startIcon={<CheckIcon />}
                        >
                            {prompt.yesLabel}
                        </Button>
                        <Button
                            onClick={() => {
                                prompt.resolve(false);
                                removePrompt(prompt.id);
                            }}
                            variant={AppPalette.VariantOutlined}
                            startIcon={<WarningAmberIcon />}
                        >
                            {prompt.noLabel}
                        </Button>
                        </>
                        )}
                    </DialogActions>
                </Dialog>
            ))}
        </PromptContext.Provider>
    );
};
